import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { api, getToken, setToken, clearToken } from '@/lib/api';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return fallback;
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('checking'); // checking | authenticated | anon | denied
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [lockedWorkplaceId, setLockedWorkplaceId] = useState(null);
  const [needsUvitanie, setNeedsUvitanie] = useState(false);
  const bootRef = useRef(false);

  useEffect(() => {
    if (bootRef.current) return; // StrictMode-safe
    bootRef.current = true;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const domena = params.get('domena') || params.get('DOMENA');
      const idp = params.get('IDP') || params.get('idp');
      const idSpravcu = params.get('id_spravcu') || params.get('ID_SPRAVCU');

      // PREVÁDZKA landing: ?IDP=X&domena=Y (bez id_spravcu)
      if (domena && idp && !idSpravcu) {
        clearToken();
        try {
          const rok = params.get('ROK') || params.get('rok');
          const mesiac = params.get('MESIAC') || params.get('mesiac');
          const { data } = await api.post('/sso/prevadzka-landing', {
            domena,
            idp: parseInt(idp, 10),
            rok: rok ? parseInt(rok, 10) : undefined,
            mesiac: mesiac ? parseInt(mesiac, 10) : undefined,
          });
          setToken(data.session_token);
          setUser({ ...data.user, kind: 'prevadzka-landing' });
          setLockedWorkplaceId(data.user.locked_workplace_id);
          setNeedsUvitanie(Boolean(data.workplace?.needs_uvitanie));
          setStatus('authenticated');
        } catch (e) {
          setError(errMsg(e, 'Nepodarilo sa načítať stredisko z TiDo.'));
          setStatus('denied');
        }
        return;
      }

      // Existujúci token → /auth/me
      const t = getToken();
      if (t) {
        try {
          const { data } = await api.get('/auth/me');
          setUser(data);
          if (data.role === 'prevadzka') setLockedWorkplaceId(data.locked_workplace_id);
          setStatus('authenticated');
          return;
        } catch (e) {
          clearToken();
        }
      }
      setStatus('anon');
    })();
  }, []);

  const login = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    setToken(data.access_token);
    setUser(data.user);
    setStatus('authenticated');
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) { /* ignore */ }
    clearToken();
    setUser(null);
    setLockedWorkplaceId(null);
    setStatus('anon');
    // odstráň prípadné TiDo query parametre
    if (window.location.search) window.history.replaceState({}, '', window.location.pathname);
  };

  return (
    <AuthContext.Provider value={{ status, user, error, lockedWorkplaceId, needsUvitanie, setNeedsUvitanie, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
